<div class="relative min-h-screen bg-slate-50 -m-4 sm:-m-8">

  @if (data()?.loading) {
    <div class="absolute inset-0 z-40 flex items-center justify-center bg-slate-50/70 backdrop-blur-[1px]"
         role="status" aria-live="polite" aria-busy="true">
      <div class="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-8 py-6 shadow-lg">
        <i class="pi pi-spin pi-spinner text-3xl text-teal-600"></i>
        <div class="text-center">
          <p class="text-sm font-semibold text-slate-800">Cargando forecast…</p>
          <p class="mt-0.5 text-xs text-slate-500">Actualizando proyección del periodo</p>
        </div>
      </div>
    </div>
  }

  <!-- Header -->
  <div class="bg-white border-b border-slate-200 px-6 py-5">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <div class="flex items-center gap-2">
          <span class="flex items-center justify-center w-9 h-9 rounded-xl bg-teal-600">
            <i class="pi pi-chart-bar text-white text-base"></i>
          </span>
          <div>
            <h1 class="text-2xl font-bold text-slate-900">Forecast de ingresos de caja</h1>
            <p class="text-xs text-slate-500">Proyección de ingresos</p>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <span class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium">
          <i class="pi pi-calendar text-slate-400"></i>
          {{ periodLabel() }}
        </span>

        <div class="flex rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm"
             [class.pointer-events-none]="data()?.loading"
             [class.opacity-60]="data()?.loading">
          @for (opt of horizonOptions; track opt.value) {
            <button type="button" (click)="setHorizon(opt.value)"
              [disabled]="data()?.loading"
              class="px-3 py-2 text-xs font-semibold transition-colors disabled:cursor-wait"
              [class.bg-teal-600]="period().horizon === opt.value && opt.value !== 'custom'"
              [class.text-white]="period().horizon === opt.value && opt.value !== 'custom'"
              [class.text-slate-600]="period().horizon !== opt.value"
              [class.hover:bg-slate-50]="period().horizon !== opt.value && !data()?.loading">
              {{ opt.label }}
            </button>
          }
        </div>

        <button type="button" (click)="load()" [disabled]="data()?.loading"
          class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 text-xs font-medium disabled:opacity-50 shadow-sm">
          <i class="pi pi-refresh text-sm" [class.animate-spin]="data()?.loading"></i>
          Actualizar
        </button>
      </div>
    </div>

    @if (period().horizon === 'custom') {
      <div class="mt-4 flex flex-wrap items-center gap-3 ml-11">
        <input type="date" [value]="customDateFrom()"
          (input)="customDateFrom.set($any($event.target).value)"
          class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500">
        <span class="text-slate-400 text-sm">—</span>
        <input type="date" [value]="customDateTo()"
          (input)="customDateTo.set($any($event.target).value)"
          class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500">
        <button type="button" (click)="applyCustomPeriod()"
          [disabled]="!customDateFrom() || !customDateTo() || !!data()?.loading"
          class="px-4 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
          Aplicar
        </button>
      </div>
    }
  </div>

  <!-- Idea strip -->
  <div class="bg-teal-700 text-white px-6 py-3 text-sm">
    <div class="flex flex-wrap items-center gap-x-8 gap-y-2">
      <span class="flex items-start gap-2 max-w-3xl">
        <i class="pi pi-info-circle mt-0.5 opacity-80"></i>
        <span>
          Combina <strong>cargos ya calendarizados</strong> (ventas a plazos / periodo actual)
          con <strong>renovaciones proyectadas</strong> de suscripciones activas
          (Siguiente fecha de cobro + frecuencia),
          ponderadas por riesgo de cobro.
        </span>
      </span>
      @if (data()?.kpis; as k) {
        <span class="flex items-center gap-2.5 ml-auto shrink-0">
          <span class="opacity-90 inline-flex items-center gap-1.5 text-xs">
            Cobrado en el periodo
            <i class="pi pi-question-circle text-teal-100/90 cursor-help text-xs"
              [pTooltip]="help.collected"
              tooltipPosition="left"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </span>
          <strong class="text-xl sm:text-2xl font-bold tabular-nums text-white leading-none">
            {{ k.collectedInHorizon | currency:'MXN':'symbol-narrow':'1.0-0' }}
          </strong>
        </span>
      }
    </div>
  </div>

  @if (data()?.error) {
    <div class="mx-6 mt-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-amber-500"></i>
      {{ data()?.error }}
    </div>
  }

  <div class="p-6 space-y-5">

    <!-- KPIs -->
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            Proyección bruta
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem] font-normal normal-case tracking-normal"
              [pTooltip]="help.projection"
              tooltipPosition="bottom"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
            <i class="pi pi-wallet text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.totalProjected ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-slate-500">
          {{ data()?.kpis?.eventCount ?? 0 }} eventos ·
          esperado {{ (data()?.kpis?.totalExpected ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
      </div>

      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            Ingresos mensuales (MRR)
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem] font-normal normal-case tracking-normal"
              [pTooltip]="help.mrr"
              tooltipPosition="bottom"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center">
            <i class="pi pi-sync text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.mrr ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-slate-500">
          {{ data()?.kpis?.activeSubscriptions ?? 0 }} suscripciones activas/trial
        </p>
      </div>

      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            Cargos calendarizados
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem] font-normal normal-case tracking-normal"
              [pTooltip]="help.charges"
              tooltipPosition="bottom"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <i class="pi pi-calendar-plus text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.chargesProjected ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-slate-500">
          Suscripciones proyectadas:
          {{ (data()?.kpis?.subscriptionsProjected ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
      </div>

      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
            En riesgo / vencido
            <i class="pi pi-question-circle text-slate-400 cursor-help text-[0.7rem] font-normal normal-case tracking-normal"
              [pTooltip]="help.risk"
              tooltipPosition="left"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </p>
          <span class="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
            <i class="pi pi-exclamation-triangle text-sm"></i>
          </span>
        </div>
        <p class="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
          {{ (data()?.kpis?.atRiskAmount ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
        <p class="mt-1 text-xs text-slate-500">
          Vencido fuera del periodo:
          {{ (data()?.kpis?.overdueAmount ?? 0) | currency:'MXN':'symbol-narrow':'1.0-0' }}
        </p>
      </div>
    </div>

    <!-- Chart + mix -->
    <div class="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <div class="xl:col-span-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div class="flex items-center justify-between mb-3">
          <div>
            <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
              Ingresos de caja proyectados
              <i class="pi pi-question-circle text-slate-400 cursor-help text-xs font-normal"
                [pTooltip]="help.cashflow"
                tooltipPosition="right"
                tooltipStyleClass="forecast-help-tooltip"
                [showDelay]="200"></i>
            </h2>
            <p class="text-xs text-slate-500">Cobrado + cargos pendientes + renovaciones por mes</p>
          </div>
          @if (data()?.loading) {
            <i class="pi pi-spin pi-spinner text-slate-400"></i>
          }
        </div>
        <div class="h-72">
          <p-chart type="bar" [data]="cashflowChart()" [options]="cashflowOptions" styleClass="h-full w-full" />
        </div>
      </div>

      <div class="xl:col-span-4 space-y-4">
        <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 overflow-hidden">
          <h2 class="text-sm font-semibold text-slate-900 mb-1 inline-flex items-center gap-1.5">
            Composición
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs font-normal"
              [pTooltip]="help.composition"
              tooltipPosition="left"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </h2>
          <p class="text-xs text-slate-500 mb-3">Cobrado vs pendiente vs proyección</p>
          <div class="flex items-center gap-3 min-h-0">
            <div class="relative shrink-0 w-28 h-28 overflow-hidden">
              <p-chart type="doughnut" [data]="mixChart()" [options]="doughnutOptions" class="block h-full w-full" />
            </div>
            <ul class="min-w-0 flex-1 space-y-2">
              @for (seg of mixLegend(); track seg.label) {
                <li class="flex items-center gap-2 text-xs">
                  <span class="w-2.5 h-2.5 rounded-full shrink-0" [style.background]="seg.color"></span>
                  <span class="text-slate-600 truncate">{{ seg.label }}</span>
                  <span class="font-semibold text-slate-900 ml-auto tabular-nums pl-2">
                    {{ seg.amount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                  </span>
                </li>
              }
            </ul>
          </div>
        </div>
        <div class="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 overflow-hidden">
          <h2 class="text-sm font-semibold text-slate-900 mb-3 inline-flex items-center gap-1.5">
            Por método de cobro
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs font-normal"
              [pTooltip]="help.method"
              tooltipPosition="left"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </h2>
          <div class="flex items-center gap-3 min-h-0">
            <div class="relative shrink-0 w-28 h-28 overflow-hidden">
              <p-chart type="doughnut" [data]="methodChart()" [options]="doughnutOptions" class="block h-full w-full" />
            </div>
            <ul class="min-w-0 flex-1 space-y-2 max-h-28 overflow-y-auto">
              @for (seg of methodLegend(); track seg.label) {
                <li class="flex items-center gap-2 text-xs">
                  <span class="w-2.5 h-2.5 rounded-full shrink-0" [style.background]="seg.color"></span>
                  <span class="text-slate-600 truncate" [pTooltip]="seg.label">{{ seg.label }}</span>
                  <span class="font-semibold text-slate-900 ml-auto tabular-nums pl-2">
                    {{ seg.amount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                  </span>
                </li>
              } @empty {
                <li class="text-xs text-slate-400">Sin datos</li>
              }
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Monthly table + narrative summary -->
    <div class="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <div class="xl:col-span-7 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div class="px-4 py-3 border-b border-slate-100">
          <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            Desglose mensual
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs font-normal"
              [pTooltip]="help.monthly"
              tooltipPosition="right"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </h2>
        </div>
        <p-table [value]="data()?.months ?? []" styleClass="p-datatable-sm" [paginator]="false">
          <ng-template pTemplate="header">
            <tr>
              <th class="text-left">Mes</th>
              <th class="text-right">Cobrado</th>
              <th class="text-right hidden sm:table-cell">Cargos</th>
              <th class="text-right hidden sm:table-cell">Suscripciones</th>
              <th class="text-right">Proyectado</th>
              <th class="text-right">Esperado</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td class="text-left">
                <span class="inline-flex items-center gap-2">
                  <i class="pi pi-calendar text-teal-600 text-xs"></i>
                  {{ row.label }}
                </span>
              </td>
              <td class="text-right tabular-nums font-semibold text-teal-800">
                {{ row.collectedAmount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                <span class="text-slate-400 text-xs ml-1">({{ row.collectedCount }})</span>
              </td>
              <td class="text-right tabular-nums hidden sm:table-cell">
                {{ row.chargesAmount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                <span class="text-slate-400 text-xs ml-1">({{ row.chargeCount }})</span>
              </td>
              <td class="text-right tabular-nums hidden sm:table-cell">
                {{ row.subscriptionsAmount | currency:'MXN':'symbol-narrow':'1.0-0' }}
                <span class="text-slate-400 text-xs ml-1">({{ row.subscriptionCount }})</span>
              </td>
              <td class="text-right tabular-nums font-semibold">
                {{ row.totalAmount | currency:'MXN':'symbol-narrow':'1.0-0' }}
              </td>
              <td class="text-right tabular-nums text-amber-700">
                {{ row.expectedAmount | currency:'MXN':'symbol-narrow':'1.0-0' }}
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6" class="text-center text-slate-400 py-8 text-sm">
                Sin movimientos en el periodo seleccionado
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <div class="xl:col-span-5 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div class="px-4 py-3 border-b border-slate-100">
          <h2 class="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            Resumen
            <i class="pi pi-question-circle text-slate-400 cursor-help text-xs font-normal"
              [pTooltip]="help.summary"
              tooltipPosition="left"
              tooltipStyleClass="forecast-help-tooltip"
              [showDelay]="200"></i>
          </h2>
          <p class="text-xs text-slate-500">Lectura rápida del forecast</p>
        </div>
        <div class="px-4 py-4 space-y-3 text-sm leading-relaxed text-slate-700">
          @for (p of narrativeSummary(); track $index) {
            <p>{{ p }}</p>
          }
        </div>
      </div>
    </div>

    <!-- Glosario -->
    <footer class="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <button type="button"
        class="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        (click)="toggleGlossary()"
        [attr.aria-expanded]="glossaryOpen()">
        <div>
          <h2 class="text-sm font-semibold text-slate-900">Términos usados</h2>
          <p class="mt-0.5 text-xs text-slate-500">
            Definiciones cortas de las métricas que aparecen en esta pantalla.
          </p>
        </div>
        <i class="pi text-slate-400 text-sm shrink-0"
          [class.pi-chevron-down]="!glossaryOpen()"
          [class.pi-chevron-up]="glossaryOpen()"></i>
      </button>
      @if (glossaryOpen()) {
        <dl class="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 border-t border-slate-100 pt-4">
          @for (item of glossary; track item.term) {
            <div>
              <dt class="text-xs font-semibold uppercase tracking-wide text-teal-700">{{ item.term }}</dt>
              <dd class="mt-1 text-sm leading-relaxed text-slate-600">{{ item.definition }}</dd>
            </div>
          }
        </dl>
      }
    </footer>

  </div>
</div>
